import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Empty, Popover, Typography } from "antd";
import { BellFilled, BellOutlined } from "@ant-design/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getEcho } from "../bootstrap/echo";
import { setEchoAuthToken } from "../bootstrap/echoAuth";
import NotificationWithIcon from "./NotificationWithIcon";
import { showWarningMessage } from "../utils/antdMessage";
import {
  TransactionOrderSource,
  TransactionStatus,
} from "../configs/constants";
import "./PendingQrOrderNotifications.css";

const EVENT_NAME = ".new-pending-qr-order";
const NOTIFICATION_STORAGE_PREFIX = "pending-qr-order-notifications";
const SOUND_STORAGE_PREFIX = "pending-qr-order-notification-sound";
const MAX_STORED_READ_NOTIFICATIONS = 100;

const getOrderId = (payload) =>
  payload?.order_id ?? payload?.transaction_id ?? payload?.id ?? null;

const getOrderKey = (payload) =>
  String(
    getOrderId(payload) ||
      payload?.order_number ||
      `${payload?.created_at || "unknown"}-${payload?.amount_charged || 0}`,
  );

const isPendingQrOrder = (payload) =>
  Number(payload?.order_source) === TransactionOrderSource.QrOrder &&
  Number(payload?.status) === TransactionStatus.Pending;

const formatAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};

const formatNotificationTime = (value) => {
  const date = moment(value);
  return date.isValid() ? date.local().format("DD MMM YYYY, hh:mm A") : "-";
};

const isolateLtrText = (value) => `\u2066${String(value)}\u2069`;

const getStorageKey = (userId, branchId) =>
  `${NOTIFICATION_STORAGE_PREFIX}:${userId || "unknown"}:${branchId}`;

const getSoundStorageKey = (userId) =>
  `${SOUND_STORAGE_PREFIX}:${userId || "unknown"}`;

const loadSoundPreference = (storageKey) => {
  try {
    const saved = localStorage.getItem(storageKey);
    return saved !== null ? JSON.parse(saved) === true : false;
  } catch {
    return false;
  }
};

const trimStoredNotifications = (items) => {
  let retainedReadCount = 0;

  return items.filter((item) => {
    if (!item.is_read) return true;
    retainedReadCount += 1;
    return retainedReadCount <= MAX_STORED_READ_NOTIFICATIONS;
  });
};

const loadStoredNotifications = (storageKey) => {
  try {
    const storedItems = JSON.parse(localStorage.getItem(storageKey) || "[]");
    if (!Array.isArray(storedItems)) return [];

    return trimStoredNotifications(
      storedItems
        .filter((item) => item && item.key)
        .map((item) => ({ ...item, is_read: item.is_read === true })),
    );
  } catch {
    return [];
  }
};

const PendingQrOrderNotifications = ({
  branchId,
  userId,
  direction = "ltr",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storageKey = getStorageKey(userId, branchId);
  const soundStorageKey = getSoundStorageKey(userId);
  const [notifications, setNotifications] = useState(() =>
    loadStoredNotifications(storageKey),
  );
  const [soundEnabled, setSoundEnabled] = useState(() =>
    loadSoundPreference(soundStorageKey),
  );
  const [popoverOpen, setPopoverOpen] = useState(false);
  const seenOrderKeysRef = useRef(
    new Set(notifications.map((item) => item.key)),
  );
  const bellRef = useRef(null);
  const soundEnabledRef = useRef(soundEnabled);
  const activeAudioRef = useRef(new Set());
  const audioWarningShownRef = useRef(false);
  const unreadCount = notifications.reduce(
    (count, item) => count + (item.is_read ? 0 : 1),
    0,
  );

  const getOrderTitle = useCallback(
    (payload) =>
      t("new_pending_qr_order", {
        orderNumber: isolateLtrText(
          payload?.order_number || payload?.order_id || "-",
        ),
      }),
    [t],
  );

  const getOrderDetails = useCallback(
    (payload) =>
      t("pending_qr_order_notification_details", {
        table: isolateLtrText(payload?.dining_table || "-"),
        amount: isolateLtrText(formatAmount(payload?.amount_charged)),
      }),
    [t],
  );

  const reportAudioError = useCallback(
    (error) => {
      console.warn("Order notification sound failed to play", error);

      if (audioWarningShownRef.current) return;
      audioWarningShownRef.current = true;

      showWarningMessage(
        error?.name === "NotAllowedError"
          ? t("notification_sound_blocked")
          : t("notification_sound_failed"),
        {
          key: "order-notification-audio-playback-error",
          duration: 6,
        },
      );
    },
    [t],
  );

  const primeBell = useCallback(async () => {
    const audio = bellRef.current;
    if (!audio || !audio.paused) return true;

    const previousVolume = audio.volume;

    try {
      audio.volume = 0;
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audioWarningShownRef.current = false;
      return true;
    } catch {
      return false;
    } finally {
      audio.volume = previousVolume;
    }
  }, []);

  const playNotificationSound = useCallback(
    async ({ force = false } = {}) => {
      if (!force && !soundEnabledRef.current) return false;

      const template = bellRef.current;
      if (!template) return false;

      const audio = template.paused ? template : template.cloneNode(true);
      const isClone = audio !== template;

      const cleanupClone = () => {
        if (!isClone) return;
        audio.removeEventListener("ended", cleanupClone);
        audio.removeEventListener("error", cleanupClone);
        activeAudioRef.current.delete(audio);
      };

      if (isClone) {
        activeAudioRef.current.add(audio);
        audio.addEventListener("ended", cleanupClone, { once: true });
        audio.addEventListener("error", cleanupClone, { once: true });
      }

      try {
        audio.currentTime = 0;
        await audio.play();
        audioWarningShownRef.current = false;
        return true;
      } catch (error) {
        cleanupClone();
        reportAudioError(error);
        return false;
      }
    },
    [reportAudioError],
  );

  const stopNotificationSounds = useCallback(() => {
    if (bellRef.current) {
      bellRef.current.pause();
      bellRef.current.currentTime = 0;
    }

    activeAudioRef.current.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
    activeAudioRef.current.clear();
  }, []);

  const toggleSound = () => {
    const next = !soundEnabledRef.current;

    soundEnabledRef.current = next;
    setSoundEnabled(next);

    try {
      localStorage.setItem(soundStorageKey, JSON.stringify(next));
    } catch {}

    if (next) {
      void playNotificationSound({ force: true });
    } else {
      stopNotificationSounds();
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(notifications));
    } catch {}
  }, [notifications, storageKey]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    bellRef.current = new Audio("/sounds/bell.mp3");
    bellRef.current.preload = "auto";
    bellRef.current.load();

    return () => {
      stopNotificationSounds();
      bellRef.current = null;
    };
  }, [stopNotificationSounds]);

  useEffect(() => {
    if (!soundEnabled) return undefined;

    const removeUnlockListeners = () => {
      document.removeEventListener("pointerdown", unlockAudio, true);
      document.removeEventListener("keydown", unlockAudio, true);
    };

    const unlockAudio = () => {
      removeUnlockListeners();
      void primeBell();
    };

    document.addEventListener("pointerdown", unlockAudio, true);
    document.addEventListener("keydown", unlockAudio, true);

    return removeUnlockListeners;
  }, [soundEnabled, primeBell]);

  useEffect(() => {
    if (!branchId) return undefined;

    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return undefined;

    const echo = getEcho();
    if (!echo || typeof echo.private !== "function") return undefined;

    setEchoAuthToken(accessToken);

    const channelName = `pos.${branchId}.orders`;
    const channel = echo.private(channelName);
    let subscriptionActive = true;

    const handlePendingQrOrder = (payload) => {
      if (!subscriptionActive || !isPendingQrOrder(payload)) return;

      const orderKey = getOrderKey(payload);
      if (seenOrderKeysRef.current.has(orderKey)) return;

      seenOrderKeysRef.current.add(orderKey);

      const notificationItem = {
        key: orderKey,
        order_id: getOrderId(payload),
        order_number: payload?.order_number,
        dining_table: payload?.dining_table,
        amount_charged: payload?.amount_charged,
        created_at: payload?.created_at,
        received_at: new Date().toISOString(),
        is_read: false,
      };

      setNotifications((current) =>
        trimStoredNotifications([notificationItem, ...current]),
      );

      NotificationWithIcon(
        "info",
        getOrderTitle(payload),
        getOrderDetails(payload),
      );
      void playNotificationSound();
    };

    channel.listen(EVENT_NAME, handlePendingQrOrder);

    return () => {
      subscriptionActive = false;

      try {
        channel.stopListening(EVENT_NAME, handlePendingQrOrder);
      } catch {}

      try {
        echo.leave(channelName);
      } catch {}
    };
  }, [
    branchId,
    getOrderDetails,
    getOrderTitle,
    playNotificationSound,
  ]);

  if (!branchId) return null;

  const handleNotificationClick = (item) => {
    const orderId = getOrderId(item);
    if (orderId === null || orderId === undefined || orderId === "") return;

    setNotifications((current) =>
      trimStoredNotifications(
        current.map((notification) =>
          notification.key === item.key
            ? { ...notification, is_read: true }
            : notification,
        ),
      ),
    );
    setPopoverOpen(false);
    navigate(`/orders/${encodeURIComponent(String(orderId))}`);
  };

  const handleReadAll = () => {
    if (!unreadCount) return;

    setNotifications((current) =>
      trimStoredNotifications(
        current.map((notification) => ({
          ...notification,
          is_read: true,
        })),
      ),
    );
  };

  const notificationPanel = (
    <div
      className="pending-qr-notifications-panel"
      dir={direction === "rtl" ? "rtl" : "ltr"}
    >
      <div className="pending-qr-notifications-header">
        <div className="pending-qr-notifications-header-title">
          <Typography.Text strong>{t("notifications")}</Typography.Text>
          <Button
            type={soundEnabled ? "primary" : "default"}
            shape="circle"
            size="small"
            className="pending-qr-notifications-sound-toggle"
            icon={soundEnabled ? <BellFilled /> : <BellOutlined />}
            onClick={toggleSound}
            aria-pressed={soundEnabled}
            aria-label={
              soundEnabled
                ? t("notification_sound_on")
                : t("notification_sound_off")
            }
            title={
              soundEnabled
                ? t("notification_sound_on")
                : t("notification_sound_off")
            }
          />
        </div>
        <Button
          type="link"
          size="small"
          className="pending-qr-notifications-read-all"
          onClick={handleReadAll}
          disabled={!unreadCount}
        >
          {t("mark_all_as_read")}
        </Button>
      </div>

      {notifications.length ? (
        <div className="pending-qr-notifications-list">
          {notifications.map((item) => (
            <button
              type="button"
              className={`pending-qr-notification-item ${
                item.is_read
                  ? "pending-qr-notification-item-read"
                  : "pending-qr-notification-item-unread"
              }`}
              key={item.key}
              onClick={() => handleNotificationClick(item)}
              aria-label={getOrderTitle(item)}
            >
              <div className="pending-qr-notification-icon">
                <BellOutlined />
              </div>
              <div className="pending-qr-notification-content">
                <Typography.Text strong className="pending-qr-notification-title">
                  {getOrderTitle(item)}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  className="pending-qr-notification-details"
                >
                  {getOrderDetails(item)}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  className="pending-qr-notification-time"
                  dir="ltr"
                >
                  {formatNotificationTime(item.created_at || item.received_at)}
                </Typography.Text>
              </div>
              {!item.is_read && (
                <span
                  className="pending-qr-notification-unread-marker"
                  aria-hidden="true"
                />
              )}
            </button>
          ))}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("no_notifications")}
          className="pending-qr-notifications-empty"
        />
      )}
    </div>
  );

  return (
    <Popover
      content={notificationPanel}
      trigger="click"
      placement={direction === "rtl" ? "bottomLeft" : "bottomRight"}
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      overlayClassName="pending-qr-notifications-popover"
    >
      <Badge count={unreadCount} overflowCount={99} size="small">
        <Button
          type="default"
          shape="circle"
          className="pending-qr-notifications-trigger"
          icon={<BellOutlined />}
          aria-label={t("notifications")}
          title={t("notifications")}
        />
      </Badge>
    </Popover>
  );
};

export default PendingQrOrderNotifications;
